import React, { useEffect, useMemo, useCallback, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import TableServer from '../../../components/table/TableServer';
import {
    Block,
    FlexDiv,
    ThemedButton,
    LightTooltip,
    TextTiny,
    TextDefault,
    TextWithTooltipIcon,
    palette,
} from '@awarego-ui/components';
import { caseAndDiacriticsInsensitiveSort } from '../../../components/table/table-helpers';
import { useStores } from '../../../hooks/use-stores';
import moment from 'moment';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FILE_TYPES, REPORT_EXECUTION_STATUS } from '../../../utils/constants';
import { createNoReportsView } from '../reports-helpers';

const PDF_ROW_HEIGHT = 32;
const PDF_ROW_GAP = 8;
const PDF_LIST_MAX_HEIGHT =
    10.5 * PDF_ROW_HEIGHT + 9.5 * PDF_ROW_GAP;

function RecipientPdfList({ executionId, pdfUrls, recipients, onDownloadPdf, csvButton }) {
    const listRef = useRef(null);
    const [openTooltipIndex, setOpenTooltipIndex] = useState(null);

    useEffect(() => {
        const el = listRef.current;
        if (!el) return undefined;
        const closeTooltip = () => setOpenTooltipIndex(null);
        el.addEventListener('scroll', closeTooltip, { passive: true });
        return () => el.removeEventListener('scroll', closeTooltip);
    }, []);

    return (
        <FlexDiv
            column
            gap={8}
            alignStretch
            style={{
                width: '100%',
                maxWidth: '100%',
                minWidth: 0,
                overflow: 'hidden',
            }}
        >
            <div
                ref={listRef}
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
                    columnGap: 16,
                    rowGap: PDF_ROW_GAP,
                    maxHeight: PDF_LIST_MAX_HEIGHT,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    width: '100%',
                    paddingRight: 4,
                }}
            >
                {pdfUrls.map((_, index) => {
                    const email = recipients[index] || `Report ${index + 1}`;
                    return (
                        <div
                            key={`${executionId}-${index}`}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: 'auto minmax(0, 1fr)',
                                alignItems: 'center',
                                columnGap: 8,
                                minWidth: 0,
                                height: PDF_ROW_HEIGHT,
                            }}
                        >
                            <ThemedButton
                                mode="secondary"
                                size="tiny"
                                text="PDF"
                                onClick={() => onDownloadPdf(index)}
                            />
                            <LightTooltip
                                title={email}
                                disableInteractive
                                open={openTooltipIndex === index}
                                onOpen={() => setOpenTooltipIndex(index)}
                                onClose={() =>
                                    setOpenTooltipIndex((current) =>
                                        current === index ? null : current
                                    )
                                }
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: 'hide',
                                                enabled: true,
                                            },
                                        ],
                                    },
                                }}
                            >
                                <span
                                    style={{
                                        display: 'block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        minWidth: 0,
                                    }}
                                >
                                    {email}
                                </span>
                            </LightTooltip>
                        </div>
                    );
                })}
            </div>
            {csvButton}
        </FlexDiv>
    );
}

function ReportTable({ companyId, reportId }) {
    const { reportsStore } = useStores();
    const rowsPerPage = 5;

    useEffect(() => {
        void reportsStore.setQueryContext({
            p: 0,
            n: 5,
            order: 'id desc',
        });
    }, []);

    useEffect(() => {
        loadData();
        return () => {};
    }, [
        companyId,
        reportId,
        reportsStore.pageIndex,
        reportsStore.pageSize,
        reportsStore.order,
    ]);

    const loadData = useCallback(async () => {
        await reportsStore.loadExecutionsByReport(companyId, reportId);
    }, [
        companyId,
        reportId,
        reportsStore.pageIndex,
        reportsStore.pageSize,
        reportsStore.order,
    ]);

    const columns = useMemo(
        () => [
            {
                Header: 'Generated',
                accessor: 'generatedAt',
                width: 180,
                lockWidth: true,
                verticalAlign: 'top',
                Cell: (x) => {
                    return (
                        <>
                            {x.row.original.status ==
                            REPORT_EXECUTION_STATUS.ERROR ? (
                                <span
                                    style={{
                                        color: palette.graphite.ash,
                                        textDecoration: 'line-through',
                                    }}
                                >
                                    {moment(x.value).format('LL')}
                                </span>
                            ) : x.row.original.status ==
                              REPORT_EXECUTION_STATUS.PLANNED ? (
                                <span style={{ color: palette.graphite.ash }}>
                                    {moment(x.row.original.scheduledAt).format(
                                        'LL'
                                    )}
                                </span>
                            ) : (
                                <span>{moment(x.value).format('LL')}</span>
                            )}
                        </>
                    );
                },
            },
            {
                Header: 'Recipients',
                accessor: 'recipients',
                width: 130,
                lockWidth: true,
                verticalAlign: 'top',
                Cell: (x) => {
                    const recipients = Array.isArray(x.value) ? x.value : [];
                    const pdfUrls = x.row.original.pdfUrls || [];
                    if (recipients.length === 0) {
                        return null;
                    }

                    const dimmed =
                        x.row.original.status !=
                        REPORT_EXECUTION_STATUS.SUCCEEDED;
                    const struck =
                        x.row.original.status ==
                        REPORT_EXECUTION_STATUS.ERROR;

                    // Per-leader PDFs already list who each file is for.
                    if (pdfUrls.length > 1) {
                        return (
                            <TextDefault
                                lighter={dimmed}
                                style={
                                    struck
                                        ? { textDecoration: 'line-through' }
                                        : null
                                }
                            >
                                {recipients.length} recipients
                            </TextDefault>
                        );
                    }

                    return (
                        <>
                            <TextDefault
                                lighter={dimmed}
                                style={
                                    struck
                                        ? { textDecoration: 'line-through' }
                                        : null
                                }
                            >
                                {recipients.slice(0, 3).join(', ')}{' '}
                            </TextDefault>

                            {recipients.length > 3 && (
                                <LightTooltip
                                    onClick={(e) => e.preventDefault()}
                                    title={recipients
                                        .slice(3, recipients.length)
                                        .map((email) => (
                                            <>
                                                <span
                                                    style={
                                                        struck
                                                            ? {
                                                                  textDecoration:
                                                                      'line-through',
                                                                  color: palette
                                                                      .graphite
                                                                      .ash,
                                                              }
                                                            : null
                                                    }
                                                >
                                                    {email}
                                                </span>
                                                <br />
                                            </>
                                        ))}
                                >
                                    <TextTiny
                                        lighter
                                        underline
                                        style={{
                                            cursor: 'pointer',
                                        }}
                                    >
                                        +{recipients.length - 3} more
                                    </TextTiny>
                                </LightTooltip>
                            )}
                        </>
                    );
                },
                disableSortBy: true,
                verticalAlign: 'top',
            },
            {
                Header: '',
                accessor: 'error',
                width: 40,
                lockWidth: true,
                verticalAlign: 'top',
                Cell: (x) => {
                    if (
                        x.row.original.status == REPORT_EXECUTION_STATUS.ERROR
                    ) {
                        return (
                            <TextWithTooltipIcon
                                tooltipText={
                                    'Something went wrong and we are working on getting it fixed'
                                }
                                textComponent={
                                    <TextDefault color={palette.vibrant.red}>
                                        No Report was generated
                                    </TextDefault>
                                }
                                icon={
                                    <InfoOutlinedIcon
                                        fontSize="small"
                                        htmlColor={palette.vibrant.red}
                                    />
                                }
                            />
                        );
                    }

                    return null;
                },
            },
            {
                Header: 'Download file',
                constrainWidth: true,
                width: '70%',
                verticalAlign: 'top',
                Cell: (x) => {
                    const pdfUrls = x.row.original.pdfUrls || [];
                    const recipients = Array.isArray(x.row.original.recipients)
                        ? x.row.original.recipients
                        : [];
                    const hasPDF = pdfUrls.length > 0;
                    const csvButton = x.row.original.hasCSV ? (
                        <ThemedButton
                            mode="secondary"
                            size="tiny"
                            text="CSV"
                            $airy
                            onClick={() => generateCSV(x.row.original.id)}
                        />
                    ) : null;

                    if (!hasPDF) {
                        return csvButton;
                    }

                    if (pdfUrls.length === 1) {
                        return (
                            <FlexDiv $noWrap alignCenter gap={8}>
                                <ThemedButton
                                    mode="secondary"
                                    size="tiny"
                                    text="PDF"
                                    onClick={() =>
                                        generatePDF(x.row.original.id, 0)
                                    }
                                />
                                {csvButton}
                            </FlexDiv>
                        );
                    }

                    return (
                        <RecipientPdfList
                            executionId={x.row.original.id}
                            pdfUrls={pdfUrls}
                            recipients={recipients}
                            onDownloadPdf={(index) =>
                                generatePDF(x.row.original.id, index)
                            }
                            csvButton={csvButton}
                        />
                    );
                },
                disableSortBy: true,
            },
        ],
        [caseAndDiacriticsInsensitiveSort]
    );

    const pagesTotal = useMemo(() => {
        {
            if (!reportsStore.reportExecutionsCount || !reportsStore.pageSize)
                return 0;
            const pageCount = Math.ceil(
                reportsStore.reportExecutionsCount / reportsStore.pageSize
            );
            return pageCount;
        }
    }, [reportsStore.pageSize, reportsStore.reportExecutionsCount]);

    const onChangePagingParams = (pageIndex, pageSize) => {
        if (
            reportsStore.pageIndex != pageIndex ||
            reportsStore.pageSize != pageSize
        ) {
            reportsStore.setPagingParams(pageIndex, pageSize);
        }
    };

    const onChangeSort = (sortBy) => {
        reportsStore.setSortBy(sortBy);
    };

    const generatePDF = (executionId, fileIndex) => {
        reportsStore.generateFile(
            companyId,
            reportId,
            executionId,
            FILE_TYPES.PDF,
            fileIndex
        );
    };
    const generateCSV = (executionId) => {};

    return (
        <Block gap={24} tablePadding style={{ overflowX: 'hidden' }}>
            <TableServer
                columns={columns}
                data={reportsStore.reportExecutions.slice()}
                hidePagination={
                    reportsStore.reportExecutionsCount <= rowsPerPage
                }
                defaultSortBy={'generatedAt'}
                rowsTotal={reportsStore.reportExecutionsCount}
                pagesTotal={pagesTotal}
                onChangePagingParams={onChangePagingParams}
                onChangeSort={onChangeSort}
                loadingData={reportsStore.loadingExecutions}
                fixedLayout
                NoDataComponent={createNoReportsView(
                    'This report has not been executed nor scheduled'
                )}
            />
        </Block>
    );
}

export default observer(ReportTable);
